import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { Check, Info } from "lucide-react";

// Kenya launch page (Patch 46, Part 3). Every claim is worded to the exact precision agreed:
// - "prompt in English, Swahili or Sheng" — never implies a Sheng app INTERFACE.
// - build apps that bill customers in M-Pesa/Airtel/MTN — with the merchant-agreement disclosure.
// - "pay your own subscription in M-Pesa" — shown ONLY when the M-Pesa rail is actually live.
// No "first / only / nothing like this" superlatives anywhere.
export default function Kenya() {
  const nav = useNavigate();
  const [mpesaLive, setMpesaLive] = useState(false);

  useEffect(() => {
    api.get("/billing/paystack/config").then((r) => setMpesaLive(!!r.data.mpesa_enabled)).catch(() => {});
  }, []);

  const claims = [
    {
      title: "Prompt in English, Swahili, or Sheng",
      body: "Describe what you want to build in English, Kiswahili, or Sheng. Kadana understands and confirms " +
        "back what you meant — showing “I understood this as…” — before it builds anything.",
      note: "Sheng is understood as input and confirmed back; a generated app's own interface is available in " +
        "English or Kiswahili, not Sheng.",
    },
    {
      title: "Build apps that bill your customers in M-Pesa, Airtel Money, or MTN Mobile Money",
      body: "Ask Kadana to generate an app that collects payments from your own customers over M-Pesa, Airtel " +
        "Money, or MTN Mobile Money.",
      note: "Kadana generates the integration code. A merchant agreement with the relevant provider (e.g. a " +
        "Safaricom M-Pesa merchant/Daraja approval) is your own business's responsibility — Kadana provides " +
        "the integration, not the merchant relationship.",
    },
  ];

  return (
    <div className="k-canvas" data-testid="kenya-page">
      <PublicNav />

      <section style={{ margin: "0 auto", padding: "72px 24px 24px" }}>
        <span className="k-badge k-badge-accent" data-testid="kenya-eyebrow">Kadana in Kenya</span>
        <h1 className="k-heading" data-testid="kenya-h1"
          style={{ fontSize: "clamp(36px,5vw,56px)", letterSpacing: "-.03em", margin: "14px 0 0" }}>
          Build for your own audience — in your own language and payments
        </h1>
        <p className="k-muted" style={{ fontSize: 16, marginTop: 14, maxWidth: 680 }}>
          Turn a plain-language brief into compliance-ready software. Here's exactly what that means for
          Kenyan builders — stated plainly, with nothing implied that isn't true.
        </p>
        <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
          <button className="k-btn k-btn-primary" data-testid="kenya-start" onClick={() => nav("/register")}>Start free</button>
          <button className="k-btn k-btn-ghost" data-testid="kenya-pricing" onClick={() => nav("/pricing")}>See pricing</button>
        </div>
      </section>

      <section style={{ margin: "0 auto", padding: "8px 24px 40px", display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {claims.map((c) => (
          <div key={c.title} className="k-container" data-testid="kenya-claim" style={{ padding: 20 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Check size={18} color="var(--accent)" style={{ flex: "none", marginTop: 3 }} />
              <h3 className="k-heading" style={{ fontSize: 16, margin: 0 }}>{c.title}</h3>
            </div>
            <p className="k-muted" style={{ fontSize: 14, marginTop: 8 }}>{c.body}</p>
            <p className="k-muted" data-testid="kenya-claim-note"
              style={{ fontSize: 12, marginTop: 8, display: "flex", gap: 6, alignItems: "flex-start" }}>
              <Info size={13} style={{ flex: "none", marginTop: 2 }} /> {c.note}
            </p>
          </div>
        ))}

        {/* "Pay in M-Pesa" ships ONLY when the rail is live end-to-end. */}
        <div className="k-container" data-testid="kenya-pay-mpesa" style={{ padding: 20 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Check size={18} color={mpesaLive ? "var(--accent)" : "var(--muted)"} style={{ flex: "none", marginTop: 3 }} />
            <h3 className="k-heading" style={{ fontSize: 16, margin: 0 }}>Pay your own Kadana subscription in M-Pesa</h3>
          </div>
          {mpesaLive ? (
            <p className="k-muted" style={{ fontSize: 14, marginTop: 8 }}>
              Pay for your Kadana credits and Pro subscription directly with M-Pesa, priced in KES. You'll get an
              STK push to your phone — approve it with your M-Pesa PIN and your credits are added.
            </p>
          ) : (
            <p className="k-muted" data-testid="kenya-mpesa-soon" style={{ fontSize: 14, marginTop: 8 }}>
              Coming soon: paying your own Kadana subscription in M-Pesa. We'll switch this on the moment the
              payment path is working end to end — we won't advertise it before then.
            </p>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
